import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  type LexicalEditor,
} from "lexical";
import { ALargeSmall, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarDivider,
  canvasToolbarButtonVariants,
} from "../components/toolbar";
import {
  CanvasToolbarAlignMenu,
  CanvasToolbarFormatMenu,
} from "../components/toolbar/CanvasToolbarFormatMenu";
import { applyCanvasListType } from "../components/toolbar/canvasListCommands";
import { NOTE_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import { CanvasEmojiPicker } from "./shared/CanvasEmojiPicker";
import { NodeToolbarContextMenuButton } from "./shared/NodeToolbarContextMenuButton";

const FONT_FAMILIES = [
  { name: "Simple", value: "system-ui, -apple-system, sans-serif" },
  { name: "Bookish", value: "Georgia, serif" },
  { name: "Technical", value: "'Courier New', monospace" },
  { name: "Scribbled", value: "'Comic Sans MS', cursive" },
  { name: "Modern", value: "'Inter', system-ui, sans-serif" },
];

const FONT_SIZES = [
  { name: "Small", value: "12px" },
  { name: "Medium", value: "14px" },
  { name: "Large", value: "16px" },
  { name: "Extra large", value: "20px" },
  { name: "Huge", value: "24px" },
];

interface TextNoteFloatingToolbarProps {
  visible: boolean;
  noteColor: string;
  fontFamily?: string;
  fontSize?: string;
  onColorChange: (color: string) => void;
  onFontFamilyChange: (fontFamily: string) => void;
  onFontSizeChange: (fontSize: string) => void;
  editor: LexicalEditor | null;
  onPopoverOpenChange?: (isOpen: boolean) => void;
}

export function TextNoteFloatingToolbar({
  visible,
  noteColor,
  fontFamily = "system-ui, -apple-system, sans-serif",
  fontSize = "14px",
  onColorChange,
  onFontFamilyChange,
  onFontSizeChange,
  editor,
  onPopoverOpenChange,
}: TextNoteFloatingToolbarProps) {
  const [isFontFamilyOpen, setIsFontFamilyOpen] = useState(false);
  const [isFontSizeOpen, setIsFontSizeOpen] = useState(false);
  const [customFontSize, setCustomFontSize] = useState("");
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [textAlignment, setTextAlignment] = useState<
    "left" | "center" | "right"
  >("left");

  // Refs updated synchronously before notify so parent latch never sees stale OR.
  const isColorOpenRef = useRef(false);
  const isFontFamilyOpenRef = useRef(false);
  const isFontSizeOpenRef = useRef(false);
  const isFormatOpenRef = useRef(false);
  const isAlignOpenRef = useRef(false);
  const isEmojiOpenRef = useRef(false);

  const notifyOpen = useCallback(() => {
    onPopoverOpenChange?.(
      isColorOpenRef.current ||
        isFontFamilyOpenRef.current ||
        isFontSizeOpenRef.current ||
        isFormatOpenRef.current ||
        isAlignOpenRef.current ||
        isEmojiOpenRef.current,
    );
  }, [onPopoverOpenChange]);

  const handleColorOpen = useCallback(
    (open: boolean) => {
      isColorOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFontFamilyOpen = useCallback(
    (open: boolean) => {
      isFontFamilyOpenRef.current = open;
      setIsFontFamilyOpen(open);
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFontSizeOpen = useCallback(
    (open: boolean) => {
      isFontSizeOpenRef.current = open;
      setIsFontSizeOpen(open);
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleFormatOpen = useCallback(
    (open: boolean) => {
      isFormatOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleAlignOpen = useCallback(
    (open: boolean) => {
      isAlignOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleEmojiOpen = useCallback(
    (open: boolean) => {
      isEmojiOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  useEffect(() => {
    if (!editor) return;
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          setIsBold(selection.hasFormat("bold"));
          setIsItalic(selection.hasFormat("italic"));
          setIsUnderline(selection.hasFormat("underline"));
          setIsStrikethrough(selection.hasFormat("strikethrough"));
          const anchorNode = selection.anchor.getNode();
          const element =
            anchorNode.getKey() === "root"
              ? anchorNode
              : anchorNode.getTopLevelElementOrThrow();
          if ($isElementNode(element)) {
            const formatType = element.getFormatType();
            if (formatType === "center" || formatType === "right") {
              setTextAlignment(formatType);
            } else {
              setTextAlignment("left");
            }
          }
        }
      });
    });
  }, [editor]);

  const currentFontSize =
    FONT_SIZES.find((f) => f.value === fontSize) || FONT_SIZES[1];

  const handleCustomFontSizeSubmit = useCallback(() => {
    const n = parseInt(customFontSize);
    if (!isNaN(n) && n >= 8 && n <= 72) {
      onFontSizeChange(`${n}px`);
      handleFontSizeOpen(false);
      setCustomFontSize("");
    }
  }, [customFontSize, onFontSizeChange, handleFontSizeOpen]);

  const handleBoldChange = useCallback(() => {
    editor?.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
  }, [editor]);

  const handleItalicChange = useCallback(() => {
    editor?.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
  }, [editor]);

  const handleUnderlineChange = useCallback(() => {
    editor?.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
  }, [editor]);

  const handleStrikethroughChange = useCallback(() => {
    editor?.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
  }, [editor]);

  const handleAlignChange = useCallback(
    (align: "left" | "center" | "right") => {
      editor?.dispatchCommand(FORMAT_ELEMENT_COMMAND, align);
      setTextAlignment(align);
    },
    [editor],
  );

  if (!visible) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <CanvasFloatingToolbarFrame
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest("input")) return;
          e.preventDefault();
        }}
      >
        {/* Color — chat, rename, download, and expand-to-fit have moved
            into the note's chrome (or retired); the bar keeps only what
            the chrome does not own. */}
        <CanvasColorPickerButton
          colors={NOTE_COLORS}
          activeValue={noteColor}
          tooltip="Note color"
          onColorChange={onColorChange}
          onOpenChange={handleColorOpen}
        />

        <div className="w-px h-4 bg-border mx-0.5" />

        {/* Font family */}
        <Popover open={isFontFamilyOpen} onOpenChange={handleFontFamilyOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    canvasToolbarButtonVariants({ hasDropdown: true }),
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ALargeSmall className="size-4" />
                  <ChevronDown className="size-2.5" strokeWidth={2.5} />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Typeface</TooltipContent>
          </Tooltip>
          <PopoverContent
            className="w-40 p-1"
            align="center"
            side="top"
            sideOffset={8}
            onClick={(e) => e.stopPropagation()}
          >
            {FONT_FAMILIES.map((font) => (
              <button
                key={font.value}
                onClick={() => {
                  onFontFamilyChange(font.value);
                  handleFontFamilyOpen(false);
                }}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors hover:bg-n-95/8 dark:hover:bg-n-95/10",
                  fontFamily === font.value &&
                    "bg-primary/10 text-primary font-medium",
                )}
                style={{ fontFamily: font.value }}
              >
                {font.name}
              </button>
            ))}
          </PopoverContent>
        </Popover>

        {/* Font size */}
        <Popover open={isFontSizeOpen} onOpenChange={handleFontSizeOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    canvasToolbarButtonVariants({ hasDropdown: true }),
                  )}
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="text-xs font-medium">
                    {currentFontSize.name}
                  </span>
                  <ChevronDown className="h-3 w-3" />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="top">Font size</TooltipContent>
          </Tooltip>
          <PopoverContent
            className="w-40 p-1"
            align="center"
            side="top"
            sideOffset={8}
            onClick={(e) => e.stopPropagation()}
          >
            {FONT_SIZES.map((size) => (
              <button
                key={size.value}
                onClick={() => {
                  onFontSizeChange(size.value);
                  handleFontSizeOpen(false);
                }}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors whitespace-nowrap hover:bg-n-95/8 dark:hover:bg-n-95/10",
                  fontSize === size.value &&
                    "bg-primary/10 text-primary font-medium",
                )}
              >
                {size.name}
              </button>
            ))}
            <div className="pt-1 mt-1 border-t">
              <input
                type="number"
                placeholder="16"
                min="8"
                max="72"
                value={customFontSize}
                onChange={(e) => setCustomFontSize(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCustomFontSizeSubmit();
                  e.stopPropagation();
                }}
                onClick={(e) => e.stopPropagation()}
                className="w-full px-3 py-2 text-sm rounded-lg bg-neutral-0 border outline-none focus:ring-2 focus:ring-canvas-selection"
              />
            </div>
          </PopoverContent>
        </Popover>

        <CanvasToolbarDivider />

        <CanvasToolbarFormatMenu
          isBold={isBold}
          isItalic={isItalic}
          isUnderline={isUnderline}
          isStrikethrough={isStrikethrough}
          onBoldChange={handleBoldChange}
          onItalicChange={handleItalicChange}
          onUnderlineChange={handleUnderlineChange}
          onStrikethroughChange={handleStrikethroughChange}
          onListTypeSelect={(type) => applyCanvasListType(editor, type)}
          onOpenChange={handleFormatOpen}
        />

        <CanvasToolbarAlignMenu
          alignment={textAlignment}
          onAlignChange={handleAlignChange}
          onOpenChange={handleAlignOpen}
        />

        <CanvasEmojiPicker
          editor={editor}
          onPopoverOpenChange={handleEmojiOpen}
        />

        <CanvasToolbarDivider />

        <NodeToolbarContextMenuButton />
      </CanvasFloatingToolbarFrame>
    </TooltipProvider>
  );
}
